import { z } from "zod";

/**
 * Single source of truth for "which React Hook Form errors should actually
 * block submission of a decision form."
 *
 * Used by BOTH the validity-projection effect in FormContentBlockProvider
 * (which drives the global submit-disabled flag) and that form's
 * onSubmit-invalid handler (which decides whether to persist). Sharing this one
 * predicate is what keeps the disabled button and the save gate from
 * disagreeing — the divergence that let an invalid (red) form still submit.
 *
 * Rules:
 * - budget violations (sum/equal/difference rules) land under `error.sum` with
 *   a numeric zod code (too_small / too_big / not_finite) → block
 * - dependency errors are informational — the dependent option simply isn't
 *   selected — so they must NOT block
 * - a bare custom error (e.g. a required field) → block
 */
export function getBlockingErrorPromptIds(
    errors: Record<string, any> | undefined | null,
): string[] {
    if (!errors) return [];
    return Object.keys(errors).filter((promptId) => {
        const error = errors[promptId];
        if (typeof error !== "object" || error === null) return false;

        if (error.sum) {
            return (
                error.sum.type === z.ZodIssueCode.too_small ||
                error.sum.type === z.ZodIssueCode.too_big ||
                error.sum.type === z.ZodIssueCode.not_finite
            );
        }
        if (error.dependency) return false;
        if (error.type === z.ZodIssueCode.custom) return true;
        return false;
    });
}
